import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import ProductCard from '../product/ProductCard';

const ProductCarousel = ({ products, title, buttonText = "VIEW ALL", viewAllLink = null }) => {
  const [itemsPerView, setItemsPerView] = useState(4);
  const [gap, setGap] = useState(20);
  const slideBy = 1;
  
  const safeProducts = products.map(p => ({
    ...p,
    id: p.id || p._id,
    image: p.image || 'https://images.unsplash.com/photo-1543163521-1bf539c55dd2?w=400',
    originalPrice: p.originalPrice || p.price,
    discount: p.discount || '0%',
    thumbnails: p.thumbnails || [],
    sizes: p.sizes || [],
    leftBadge: p.leftBadge || '',
    rightBadge: p.rightBadge || ''
  }));

  const duplicatedProducts = [...safeProducts, ...safeProducts, ...safeProducts];
  const [currentIndex, setCurrentIndex] = useState(safeProducts.length);
  const [isDragging, setIsDragging] = useState(false);
  const [isTouching, setIsTouching] = useState(false);
  const [startX, setStartX] = useState(0);
  const [offsetX, setOffsetX] = useState(0);
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const autoPlayRef = useRef(null);
  const autoPlayDelay = 5000;

  const [itemWidth, setItemWidth] = useState(0);

  useEffect(() => {
    const updateDimensions = () => {
      const width = window.innerWidth;
      if (width < 640) {
        setItemsPerView(2);
        setGap(10);
      } else if (width < 1024) {
        setItemsPerView(3);
        setGap(24);
      } else {
        setItemsPerView(4);
        setGap(40);
      }
    };
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, []);

  useEffect(() => {
    if (containerRef.current) {
      const containerWidth = containerRef.current.clientWidth;
      const computedStyle = getComputedStyle(containerRef.current);
      const paddingLeft = parseFloat(computedStyle.paddingLeft);
      const paddingRight = parseFloat(computedStyle.paddingRight);
      const availableWidth = containerWidth - paddingLeft - paddingRight;
      
      const totalGaps = (itemsPerView - 1) * gap;
      // Subtract 0.5px to prevent any sub‑pixel overflow
      const itemWidthPx = (availableWidth - totalGaps) / itemsPerView - 0.5;
      setItemWidth(Math.max(itemWidthPx, 0));
    }
  }, [itemsPerView, gap]);

  useEffect(() => {
    if (trackRef.current && itemWidth > 0) {
      const offset = currentIndex * (itemWidth + gap);
      trackRef.current.style.transform = `translateX(-${offset}px)`;
      trackRef.current.style.transition = transitionEnabled ? 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)' : 'none';
    }
  }, [currentIndex, itemWidth, gap, transitionEnabled]);

  useEffect(() => {
    const handleTransitionEnd = () => {
      if (currentIndex >= safeProducts.length * 2) {
        setTransitionEnabled(false);
        setCurrentIndex(currentIndex - safeProducts.length);
      } else if (currentIndex < safeProducts.length) {
        setTransitionEnabled(false);
        setCurrentIndex(currentIndex + safeProducts.length);
      }
      setTimeout(() => setTransitionEnabled(true), 50);
    };
    const track = trackRef.current;
    if (track) {
      track.addEventListener('transitionend', handleTransitionEnd);
      return () => track.removeEventListener('transitionend', handleTransitionEnd);
    }
  }, [currentIndex, safeProducts.length]);

  useEffect(() => {
    if (safeProducts.length === 0) return;
    autoPlayRef.current = setInterval(() => {
      setCurrentIndex(prev => prev + slideBy);
    }, autoPlayDelay);
    return () => clearInterval(autoPlayRef.current);
  }, [safeProducts.length, slideBy]);

  const handleTouchStart = (e) => {
    clearInterval(autoPlayRef.current);
    const touch = e.touches[0];
    setIsTouching(true);
    setStartX(touch.clientX);
    if (trackRef.current) trackRef.current.style.transition = 'none';
  };

  const handleTouchMove = (e) => {
    if (!isTouching || !containerRef.current) return;
    const touch = e.touches[0];
    const diff = (touch.clientX - startX);
    setOffsetX(diff);
    if (trackRef.current) {
      const offset = currentIndex * (itemWidth + gap) - diff;
      trackRef.current.style.transform = `translateX(-${offset}px)`;
    }
  };

  const handleTouchEnd = () => {
    if (!isTouching) return;
    const threshold = 15;
    if (offsetX < -threshold) setCurrentIndex(prev => prev + slideBy);
    else if (offsetX > threshold) setCurrentIndex(prev => prev - slideBy);
    else {
      if (trackRef.current) {
        trackRef.current.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        const offset = currentIndex * (itemWidth + gap);
        trackRef.current.style.transform = `translateX(-${offset}px)`;
      }
    }
    setIsTouching(false);
    setOffsetX(0);
    autoPlayRef.current = setInterval(() => {
      setCurrentIndex(prev => prev + slideBy);
    }, autoPlayDelay);
  };

  const handleMouseDown = (e) => {
    clearInterval(autoPlayRef.current);
    setIsDragging(true);
    setStartX(e.clientX);
    if (trackRef.current) trackRef.current.style.transition = 'none';
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !containerRef.current) return;
    const diff = (e.clientX - startX);
    setOffsetX(diff);
    if (trackRef.current) {
      const offset = currentIndex * (itemWidth + gap) - diff;
      trackRef.current.style.transform = `translateX(-${offset}px)`;
    }
  };

  const handleMouseUp = () => {
    if (!isDragging) return;
    const threshold = 15;
    if (offsetX < -threshold) setCurrentIndex(prev => prev + slideBy);
    else if (offsetX > threshold) setCurrentIndex(prev => prev - slideBy);
    else {
      if (trackRef.current) {
        trackRef.current.style.transition = 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        const offset = currentIndex * (itemWidth + gap);
        trackRef.current.style.transform = `translateX(-${offset}px)`;
      }
    }
    setIsDragging(false);
    setOffsetX(0);
    autoPlayRef.current = setInterval(() => {
      setCurrentIndex(prev => prev + slideBy);
    }, autoPlayDelay);
  };

  const handleMouseLeave = () => {
    if (isDragging) handleMouseUp();
  };

  if (!safeProducts.length) return null;

  const ButtonWrapper = viewAllLink ? Link : 'button';
  const buttonProps = viewAllLink 
    ? { to: viewAllLink } 
    : { to: '/category/new-in' };

  return (
    <section className="best-sellers w-full">
      <div className="section-header">
        <h2 className="section-title">{title}</h2>
        <ButtonWrapper {...buttonProps} className="view-all-btn">
          {buttonText} →
        </ButtonWrapper>
      </div>
      <div 
        className="product-carousel-container w-full px-1 sm:px-4 md:px-6 lg:px-8"
        ref={containerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ 
          overflow: 'hidden', 
          position: 'relative',
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          WebkitUserSelect: 'none'
        }}
      >
        <div
          className="product-carousel-track"
          ref={trackRef}
          style={{
            display: 'flex',
            gap: `${gap}px`,
            willChange: 'transform',
            backfaceVisibility: 'hidden',
            touchAction: 'none',
            pointerEvents: 'auto',
            alignItems: 'stretch'
          }}
        >
          {duplicatedProducts.map((product, idx) => (
            <div 
              key={`${product.id}-${idx}`} 
              className="carousel-item-wrapper" 
              style={{ flex: `0 0 ${itemWidth}px`, display: 'flex' }}
            >
              <ProductCard product={product} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default ProductCarousel;